import React, { useMemo } from 'react'
import { ToolkitPageLayout } from '../../../components'
import { Link, useLocation } from 'react-router-dom'

export const ScdModifyingTreatmentsPage: React.FC = () => {
  const location = useLocation()

  // Determine breadcrumbs based on the referring page
  const breadcrumbs = useMemo(() => {
    const baseCrumbs = [{ label: 'Toolkit', path: '/toolkit' }]

    // Check if coming from providers, clinics, or elsewhere
    if (location.state?.from === 'providers') {
      baseCrumbs.push(
        { label: 'Providers', path: '/toolkit/providers' },
        { label: 'Training Modules', path: '/toolkit/providers/training' }
      )
    } else if (location.state?.from === 'clinics') {
      baseCrumbs.push(
        { label: 'Clinics', path: '/toolkit/clinics' },
        { label: 'Training Modules', path: '/toolkit/clinics/training' }
      )
    }

    baseCrumbs.push({
      label: 'Sickle Cell Disease Modifying Treatments',
      path: '/toolkit/shared/scd-modifying-treatments'
    })

    return baseCrumbs
  }, [location.state])

  // Document links data - using Dropbox URLs for all files
  const documents = [
    {
      title: 'Management of SCD',
      url: 'https://www.dropbox.com/scl/fi/fzxv5k6ykwdahtx1x9d02/Management-of-SCD.pdf?rlkey=yky7s8g0kfy55x7hdcz58sksw&dl=0&dl=1',
      isPrimary: true
    },
    {
      title: 'Expert Panel Report 2014: Guidelines on Hydroxyurea',
      url: 'https://www.dropbox.com/scl/fi/791y1qn056idhpfac0edb/hydroxyurea-2.pdf?rlkey=9suan6ptj3r6xn9wt8n5kgb3i&dl=0&dl=1'
    },
    {
      title: 'New England Journal of Medicine: L-Glutamine',
      url: 'https://www.dropbox.com/scl/fi/03ua418xipk6usnjavqbk/A-Phase-3-Trial-of-l-Glutamine-in-Sickle-Cell-Disease.pdf?rlkey=jeqxfh8wmz43vtqhmht9x7w1j&dl=0&dl=1'
    },
    {
      title: 'New England Journal of Medicine: Crizanlizumab',
      url: 'https://www.dropbox.com/scl/fi/hf1zror2ltg1wzq21l5co/Crizanlizumab-for-the-Prevention-of-Pain-Crises-in-Sickle-Cell-Disease.pdf?rlkey=v3x52uqhu4gf9smx7e3yfipi5&dl=0&dl=1'
    },
    {
      title: 'ASH 2020 Blood Transfusions and SCD',
      url: 'https://www.dropbox.com/scl/fi/ls0wt2rsxr0hjtx8tiq4c/advancesadv2019001143.pdf?rlkey=1063cgk0o5n2mjfctxuffkl8l&dl=0&dl=1'
    },
    {
      title: 'NHLBI, 2014 Blood Transfusions and SCD',
      url: 'https://www.dropbox.com/scl/fi/v98r4gwcc7y12a3qdqzb5/Blood-Transfusion-and-SCD.pdf?rlkey=knk9rhg7tlzdbxs93dror7mm6&dl=0&dl=1'
    },
    {
      title: 'Therapeutic-Phlebotomy-Orderset',
      url: 'https://www.dropbox.com/scl/fi/d5vud24cpwf0cydtvni24/Therapeutic-Phlebotomy-Orderset.pdf?rlkey=dsdy7zuu2qs2pirgu4ned9jkm&dl=0&dl=1'
    },
    {
      title: 'Therapeutic-Phlebotomy-Protocol',
      url: 'https://www.dropbox.com/scl/fi/cs28sz39gjvo7uiwwow9o/Therapeutic-Phlebotomy-Protocol.pdf?rlkey=5oomlnh1qf6b3nmduttf9feq5&dl=0&dl=1'
    }
  ]

  return (
    <ToolkitPageLayout
      title="Sickle Cell Disease Modifying Treatments"
      breadcrumbs={breadcrumbs}
    >
      <div className="space-y-8">
        {/* Quote Section */}
        <div className="bg-gray-50 p-6 border-l-4 border-[#A6410A]">
          <blockquote className="text-lg italic text-gray-700">
            The goal for you is to understand the study designs, what to
            monitor, and how to overall manage FDA-approved novel therapies used
            in the management of sickle cell disease.
          </blockquote>
        </div>

        <hr className="border-t border-gray-300" />

        {/* Step 1: Main Presentation */}
        <div className="space-y-4">
          <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
            <p className="text-lg text-gray-700 mb-4">
              1. Listen and Review slides for{' '}
              <em>Disease Modifying Therapies in Sickle Cell Disease</em> by
              Sophie Lanzkron, MD, MHS.
            </p>
            <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
              <div className="flex items-center justify-between">
                <span className="font-medium text-gray-700">
                  Management of SCD
                </span>
                <a
                  href={documents[0].url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center px-4 py-2 bg-[#A6410A] text-white rounded hover:bg-[#894d35] transition-colors"
                >
                  Download
                  <svg
                    className="w-4 h-4 ml-2"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                    />
                  </svg>
                </a>
              </div>
            </div>
          </div>
        </div>

        {/* Step 2: Supplemental Materials */}
        <div className="space-y-4">
          <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
            <h2 className="text-xl font-semibold mb-4 text-[#3C2416]">
              2. Review below supplemental materials:
            </h2>
            <div className="space-y-3">
              {documents.slice(1).map((doc, index) => (
                <div
                  key={index}
                  className="bg-gray-50 p-4 rounded-lg border border-gray-200"
                >
                  <div className="flex items-center justify-between">
                    <span className="font-medium text-gray-700 flex-1 pr-4">
                      {doc.title}
                    </span>
                    <a
                      href={doc.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center px-3 py-1.5 bg-[#AF8A48] text-white rounded hover:bg-[#894d35] transition-colors text-sm"
                    >
                      Download
                      <svg
                        className="w-3.5 h-3.5 ml-1.5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                        />
                      </svg>
                    </a>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <hr className="border-t border-gray-300" />

        {/* Completion Link */}
        <div className="text-center py-8">
          <Link
            to={
              location.state?.from === 'providers'
                ? '/toolkit/providers/training'
                : location.state?.from === 'clinics'
                  ? '/toolkit/clinics/training'
                  : '/toolkit'
            }
            className="inline-block px-8 py-4 bg-[#A6410A] text-white font-semibold rounded hover:bg-[#894d35] transition-colors shadow-md hover:shadow-lg"
          >
            {location.state?.from === 'providers'
              ? 'ALL DONE? CLICK HERE TO RETURN TO THE PROVIDERS SCREEN'
              : location.state?.from === 'clinics'
                ? 'ALL DONE? CLICK HERE TO RETURN TO THE CLINICS SCREEN'
                : 'ALL DONE? CLICK HERE TO RETURN TO THE MAIN SCREEN'}
          </Link>
        </div>
      </div>
    </ToolkitPageLayout>
  )
}
